/* Estimate panel — modal over the white Architect editor. What it shows
   depends on the viewer's Architect sub-role (DESIGN10 §P4), decided by the
   single-source estimateAccess() helper in api.ts:

     • Estimator / developer / root (canEstimate) — the full workflow: send
       for estimate, edit each document, download, and APPROVE with optional
       knowledge-base feedback. (Reject was removed product-wide.)
     • Generator-only (canGenerate, not canEstimate) — send for estimate and
       download; the list shows ONLY approved documents (the server filters),
       and a friendly banner appears while an estimate is under review. No
       edit / chat / approve affordances.
     • Neither sub-role — a friendly notice; estimates aren't enabled.

   English strings, en-GB dates. Generation is a tracked async RUN (mirrors
   the poster): POST /generate returns a run (202) and the modal polls
   GET /runs/:id, showing the same «крутая плашка» stepper as RunProgress
   inside the modal. The poll loop is reentrancy-guarded (copied from
   EditorView.beginPolling) and RESUMES on open via /runs/active, so a page
   reload never loses progress. Scoped .arch-modal styles live in
   architect.css. */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { estimateApi, EstimateMeta, EstimateRun, estimateAccess } from './api';
import { stageAccess } from './stageAccess';
import { useAuth } from '../auth/AuthContext';
import {
  ApprovalNote,
  canApproveNow,
  myApprovalTime,
  useApprovalStatuses,
} from './ProjectsView';
import { fmtDate, modelDisplayEstimate, mapKnownRunMessage } from './utils';
import { RunStepper, stageStep, isOperatorStop } from './RunProgress';
import { toast } from './toast';
import EstimateDocEditor from './EstimateDocEditor';
import ApproveAsSelector, { useApprovalActor } from './ApproveAsSelector';
import { RefreshCw } from 'lucide-react';
import { StageAiButton, StageFooter, StageHeader } from './StageChrome';
import './estimate-workspace.css';

// Wire values are the API contract; the labels are what the architect
// sees (claude-opus-5-5 → "Lite", gpt-5.4 → "Max").
export const ESTIMATE_MODELS = [
  { value: 'claude-opus-5-5', label: 'Lite' },
  { value: 'gpt-5.4', label: 'Max' },
];

// Poll cadence for an in-flight estimate run (ms) — matches the poster.
const RUN_POLL_MS = 1500;

// The estimate stepper's five steps + per-step descriptions. Same shape
// as RunProgress' poster steps, worded for the estimator methodology.
const EST_STEPS = [
  'Prepare sources',
  'AI is thinking',
  'Validate',
  'Render document',
  'Save',
];
const EST_STEP_DESC = [
  'Gathering sources, knowledge and prior work',
  'The AI writes the plan following the methodology',
  'Checking the plan against the schema',
  'Turning the validated plan into the document',
  'Storing the estimate',
];

/** Human message for a failed estimate run. The backend already stores a
    white-label message in run.error (the estimator classifies provider
    flows); mapKnownRunMessage normalises the sweep/stop free-text (incl. a
    legacy-Russian message from an in-flight run) to English, then we surface
    it with a neutral fallback. */
function estimateRunErrorText(raw: string | null): string {
  return mapKnownRunMessage(raw) || 'The estimate could not be completed — try again';
}

const STATUS_LABEL: Record<EstimateMeta['status'], string> = {
  review: 'Awaiting review',
  approved: 'Approved',
  rejected: 'Rejected',
};

interface Props {
  open: boolean;
  projectId: string;
  projectName: string;
  /** DESIGN12 §F3 — whether the project's architecture is approved. Generation
      is gated on it for EVERY caller (the server enforces the same rule): the
      «Send for estimate» button is disabled with an inline hint until it's
      true. Viewing existing estimates stays open. Defaults to true so an
      out-of-date caller never blocks the button. */
  architectureApproved?: boolean;
  onClose: () => void;
  presentation?: 'modal' | 'workspace';
  active?: boolean;
  /** Keep run progress visible while the only saved documents belong to an
      earlier architecture revision. Historical documents live in History. */
  historicalOnly?: boolean;
  /** The server has already started the hand-off, even if this panel has not
      resumed its run yet. A recovery button must not race that run. */
  estimateRunning?: boolean;
  /** Invalidate reference/document reads after an upstream mutation. */
  revision?: number;
  onChanged?: () => void;
  onDraftStateChange?: (state: { dirty: boolean; busy: boolean }) => void;
}

export default function EstimatePanel({
  open,
  projectId,
  projectName,
  architectureApproved = true,
  onClose,
  presentation = 'modal',
  active = true,
  historicalOnly = false,
  estimateRunning = false,
  revision = 0,
  onChanged,
  onDraftStateChange,
}: Props) {
  const workspace = presentation === 'workspace';
  const { user, permissions } = useAuth();
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  // One automatic selection per project. Returning to history is intentional.
  const initialDocumentRef = useRef<string | null>(null);
  // The single source of the role rule (mirrors the backend gating).
  const { canEstimate, canViewEstimate, canApproveEstimate } = useMemo(
    () => estimateAccess(user, permissions),
    [user, permissions],
  );

  const [estimates, setEstimates] = useState<EstimateMeta[]>([]);
  // An empty list is safe for recovery only after a successful read for this
  // exact project and architecture revision. A failed request is not proof
  // that the current estimate does not exist.
  const [estimateListLoadedFor, setEstimateListLoadedFor] = useState<string | null>(null);
  const recoveryKey = `${projectId}:${revision}:${historicalOnly}`;
  // Count of in-review documents a generator-only viewer can't yet see —
  // drives the «оценка на проверке» banner. Only meaningful for generators.
  const [pendingReview, setPendingReview] = useState(0);
  const [loading, setLoading] = useState(false);
  // The live async run — drives the in-modal stepper and the busy state.
  const [activeRun, setActiveRun] = useState<EstimateRun | null>(null);
  // Wall-clock start (run.created_at) so a resumed run shows the true
  // elapsed time, not time-since-reopen; null when idle.
  const [runStartMs, setRunStartMs] = useState<number | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [running, setRunning] = useState(false);
  const [resumeChecked, setResumeChecked] = useState(false);
  // The last failed run's white-label message — shown inline until the
  // next attempt clears it (a toast alone would vanish too fast).
  const [runError, setRunError] = useState<string | null>(null);
  const [model, setModel] = useState(ESTIMATE_MODELS[0].value);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  // Per-row review feedback + the id whose approve is in flight.
  const [feedback, setFeedback] = useState<Record<string, string>>({});
  const [decidingId, setDecidingId] = useState<string | null>(null);
  // The just-produced estimate row, briefly flashed after a run lands.
  const [flashId, setFlashId] = useState<string | null>(null);
  // The estimate whose document is open in the full-screen editor (the
  // modal is hidden while it's up, and reopens when the editor closes).
  const [editingId, setEditingId] = useState<string | null>(null);
  const editingIdRef = useRef<string | null>(null);
  editingIdRef.current = editingId;
  const documentDraftDirty = useRef(false);
  const latestKnownId = useRef<string | null>(null);
  // DESIGN23 §C3 — bumped after an approve so the «Approved 1 of 2» counters
  // refetch even when the row set itself hasn't changed (a recorded vote
  // below the threshold leaves the estimate exactly where it was).
  const [apprRev, setApprRev] = useState(0);

  // A viewer with neither sub-role (and not dev/root) gets a notice only.
  const noAccess = !canViewEstimate;

  /* Approval progress for the rows that still carry an approve control. Only
     'review' rows are queried — an approved estimate already states its
     approver line — and a failed read simply leaves that row on today's
     behaviour (one approval, any estimator). */
  const reviewIds = useMemo(
    () =>
      canViewEstimate
        ? estimates.filter((e) => e.status === 'review' || e.id === editingId).map((e) => e.id)
        : [],
    [canViewEstimate, estimates, editingId],
  );
  const approvals = useApprovalStatuses(
    'estimate',
    'estimate',
    reviewIds,
    canViewEstimate,
    apprRev,
  );
  const approvalActor = useApprovalActor({ root: !!user?.isRoot, status: editingId ? approvals[editingId] : null,
    step: 'estimate', targetId: editingId || projectId, enabled: !!editingId });

  // setInterval id of the active poll loop (null when idle).
  const pollRef = useRef<number | null>(null);
  // The run id the poll loop is committed to (null when idle). A Stop clears
  // the run out from under an in-flight tick and a new run may start right
  // after; each tick checks this before adopting so a stale response can't
  // clobber the live run (DESIGN13 §F3).
  const pollRunIdRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setEstimateListLoadedFor(null);
    try {
      const r = await estimateApi.listEstimates(projectId);
      setEstimates(r.estimates || []);
      setEstimateListLoadedFor(recoveryKey);
      setPendingReview(r.pending_review || 0);
      const latest = [...(r.estimates || [])].sort(
        (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
      )[0];
      const newestId = latest?.id || null;
      const isNewVersion = newestId !== latestKnownId.current;
      latestKnownId.current = newestId;
      if (workspace && canViewEstimate && !historicalOnly && initialDocumentRef.current !== projectId) {
        initialDocumentRef.current = projectId;
        if (newestId) setEditingId(newestId);
      } else if (workspace && canViewEstimate && !historicalOnly && isNewVersion && newestId) {
        if (documentDraftDirty.current) toast('A newer estimate is ready in history. Save your draft before switching.', 'info');
        else setEditingId(newestId);
      }
    } catch (err: any) {
      toast(err.message, 'err');
    } finally {
      setLoading(false);
    }
  }, [projectId, workspace, canViewEstimate, historicalOnly, recoveryKey]);

  /* ── load the list whenever the modal opens (skip when no access) ── */
  useEffect(() => {
    if (open && !noAccess && (!workspace || active)) load();
  }, [open, noAccess, load, workspace, active, revision]);

  /* ── run lifecycle (async generate) ──────────────────────
     Mirrors EditorView: POST returns a run, we poll /runs/:id until
     done/error. Survives proxy timeouts and — via /runs/active on open
     — page reloads. */

  const stopPolling = useCallback(() => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const clearRun = useCallback(() => {
    stopPolling();
    pollRunIdRef.current = null;
    setRunning(false);
    setActiveRun(null);
    setRunStartMs(null);
  }, [stopPolling]);

  const beginPolling = useCallback(
    (runId: string) => {
      stopPolling();
      pollRunIdRef.current = runId;
      // Reentrancy guard: a GET slower than the poll interval means two
      // ticks can be in flight at once — without this both could see the
      // terminal status and refresh/toast twice.
      let inFlight = false;
      let settled = false;
      const tick = async () => {
        if (inFlight || settled) return;
        inFlight = true;
        let run: EstimateRun;
        try {
          run = (await estimateApi.getEstimateRun(runId)).run;
        } catch (e: any) {
          inFlight = false;
          // A lost run (404) is terminal; transient errors keep polling.
          if (e.status === 404) {
            settled = true;
            clearRun();
            toast('The estimate run was lost — try again', 'err');
            onChangedRef.current?.();
          }
          return;
        }
        inFlight = false;
        // Stopped or superseded while this GET was in flight — drop the stale
        // response so it can't re-show or clobber a newer run.
        if (settled || pollRunIdRef.current !== runId) return;
        setActiveRun(run);
        if (run.status === 'done') {
          settled = true;
          clearRun();
          await load();
          onChangedRef.current?.();
          // Flash the freshly produced row so the eye finds it.
          if (run.estimate_id) {
            const id = run.estimate_id;
            if (workspace && canViewEstimate && !historicalOnly) setEditingId((current) => current || id);
            setFlashId(id);
            window.setTimeout(
              () => setFlashId((cur) => (cur === id ? null : cur)),
              1400,
            );
          }
          // A generator-only viewer won't see the new (in-review) row yet.
          toast(
            canEstimate
              ? 'Estimate ready — review it below'
              : 'Estimate sent — the estimator will review it',
            'ok',
          );
        } else if (run.status === 'error') {
          settled = true;
          clearRun();
          onChangedRef.current?.();
          // An operator stop is not a failure (reaches here when stopped in
          // another tab; §F4) — clear the inline error and toast neutrally.
          if (isOperatorStop(run.error)) {
            setRunError(null);
            toast('Generation stopped');
          } else {
            const msg = estimateRunErrorText(run.error);
            setRunError(msg);
            toast(msg, 'err');
          }
        }
      };
      tick(); // poll immediately, then on the interval
      pollRef.current = window.setInterval(tick, RUN_POLL_MS);
    },
    [stopPolling, clearRun, load, canEstimate, canViewEstimate, workspace, historicalOnly],
  );

  /* ── RESUME: on open, pick up any still-running estimate run so a
        page reload continues the stepper instead of showing idle ── */
  useEffect(() => {
    if (!open || noAccess || (workspace && !active)) return undefined;
    let cancelled = false;
    setResumeChecked(false);
    (async () => {
      try {
        const { run } = await estimateApi.getActiveEstimateRun(projectId);
        if (cancelled || !run || run.status !== 'running') return;
        setActiveRun(run);
        setRunStartMs(Date.parse(run.created_at) || Date.now());
        setRunning(true);
        setRunError(null);
        beginPolling(run.id);
      } catch (_) {
        /* no active-run info — nothing to resume */
      } finally {
        if (!cancelled) setResumeChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, noAccess, projectId, beginPolling, workspace, active, revision]);

  /* ── stop polling if the panel unmounts ──────────────── */
  useEffect(() => () => stopPolling(), [stopPolling]);

  /* ── elapsed ticker driven by the run's wall-clock start ── */
  useEffect(() => {
    if (runStartMs == null) {
      setElapsedMs(0);
      return undefined;
    }
    const upd = () => setElapsedMs(Math.max(0, Date.now() - runStartMs));
    upd();
    const t = window.setInterval(upd, 1000);
    return () => window.clearInterval(t);
  }, [runStartMs]);

  /* ── close on Escape (never while an estimate runs or the document
        editor is open — the editor owns Escape then) ──── */
  useEffect(() => {
    if (!open || !active || workspace) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !running && !editingId) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, active, workspace, running, editingId, onClose]);

  /* ── drop any in-flight editor when the panel itself closes ── */
  useEffect(() => {
    if (!open) setEditingId(null);
  }, [open]);

  const closeEditor = useCallback(
    (changed: boolean) => {
      setEditingId(null);
      if (changed) load(); // status is back to review — refresh the list
    },
    [load],
  );

  const requestClose = useCallback(() => {
    if (running) return; // don't abandon a live estimate run
    onClose();
  }, [running, onClose]);

  const generate = useCallback(async () => {
    if (running) return;
    setRunning(true);
    setRunError(null); // a fresh attempt clears the previous failure
    try {
      const { run } = await estimateApi.generateEstimate(projectId, model);
      setActiveRun(run);
      setRunStartMs(Date.parse(run.created_at) || Date.now());
      beginPolling(run.id);
    } catch (err: any) {
      toast(err.message, 'err');
      setRunning(false);
    }
  }, [running, projectId, model, beginPolling]);

  /** Stop the active estimate run (DESIGN13 §F3). Confirmed inside the
      stepper's button; cancel is idempotent server-side and returns the
      updated run. If it came back stopped, clear the stepper immediately and
      toast neutrally — the locks are released so a new run can start at once.
      A run that raced to a natural finish is handed back to the poll loop. */
  const stopRun = useCallback(async () => {
    const run = activeRun;
    if (!run) return;
    try {
      const { run: stopped } = await estimateApi.cancelEstimateRun(run.id);
      if (isOperatorStop(stopped.error)) {
        clearRun();
        setRunError(null);
        toast('Generation stopped');
      } else {
        setActiveRun(stopped);
      }
    } catch (err: any) {
      toast(err.message, 'err');
    }
  }, [activeRun, clearRun]);

  const download = useCallback(async (est: EstimateMeta) => {
    setDownloadingId(est.id);
    try {
      const res = await estimateApi.estimateDocxBlob(est.id);
      const blob = await res.blob();
      let filename = (est.title || 'Estimate') + '.docx';
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      if (m) filename = decodeURIComponent(m[1]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast('Document downloaded', 'ok');
    } catch (err: any) {
      toast(err.message, 'err');
    } finally {
      setDownloadingId(null);
    }
  }, []);

  /* Approve is the ONLY estimate decision (Reject was removed product-wide).
     DESIGN23 §C3 — with a configured multi-approver policy the server records
     the vote and deliberately LEAVES the estimate in 'review' until the
     threshold is met; the response's `approvals` block says how far it got.
     With no policy (the default) `approvals` is absent or already met and the
     flow is byte-for-byte today's. */
  const approve = useCallback(
    async (est: EstimateMeta) => {
      if (decidingId) return;
      const note = (feedback[est.id] || '').trim();
      setDecidingId(est.id);
      try {
        const r = await estimateApi.approveEstimate(est.id, note || undefined, est.id === editingId ? approvalActor.body : {});
        setFeedback((prev) => {
          const next = { ...prev };
          delete next[est.id];
          return next;
        });
        const progress = r?.approvals;
        const met = !progress || progress.given >= progress.required;
        // The completed estimate prepares the project pricing draft. The
        // estimator does not see commercial values; root receives the handoff.
        const autoPricing = r?.auto_started === 'pricing';
        toast(
          !met
            ? 'Your approval is recorded — ' +
                progress!.given +
                ' of ' +
                progress!.required
            : note
              ? 'Estimate approved — feedback sent to the knowledge base'
              : autoPricing
                ? 'Estimate approved — pricing is ready for review'
                : 'Estimate approved',
          'ok',
        );
        await load();
        onChangedRef.current?.();
      } catch (err: any) {
        toast(err.message, 'err');
      } finally {
        setDecidingId(null);
        // Refresh the counters either way — a 403 «not an approver» is itself
        // news the row should reflect.
        setApprRev((n) => n + 1);
      }
    },
    [decidingId, feedback, load, editingId, approvalActor.body],
  );

  const renderReview = (est: EstimateMeta) => {
    if (!canApproveEstimate || (stageAccess(user, permissions, 'estimate').configured && !approvals[est.id])) return null;
    const st = approvals[est.id];
    const mineAt = myApprovalTime(st, user?.id);
    const showApprove = user?.isRoot && est.id === editingId ? approvalActor.canApprove : canApproveNow(st) && !mineAt;
    return (
      <div className="arch-est-review">
        {user?.isRoot && est.id === editingId ? <ApproveAsSelector actor={approvalActor} disabled={decidingId === est.id}/> : null}
        {!workspace ? <ApprovalNote
          status={st}
          userId={user?.id}
          pending
          className="arch-appr-row"
        /> : null}
        {showApprove ? (
          <>
            {workspace ? <details className="arch-est-review-feedback"><summary>Add feedback (optional)</summary><textarea
              className="arch-full-width arch-modal-textarea"
              rows={2}
              placeholder="Feedback for the estimator knowledge base (applies after root approval in Feedback)"
              value={feedback[est.id] || ''}
              disabled={decidingId === est.id}
              onChange={(e) =>
                setFeedback((prev) => ({
                  ...prev,
                  [est.id]: e.target.value,
                }))
              }
            /></details> : <textarea
              className="arch-full-width arch-modal-textarea"
              rows={2}
              placeholder="Feedback for the estimator knowledge base (applies after root approval in Feedback)"
              value={feedback[est.id] || ''}
              disabled={decidingId === est.id}
              onChange={(e) => setFeedback((prev) => ({ ...prev, [est.id]: e.target.value }))}
            />}
            <div className="arch-est-decide">
              <button
                className="arch-btn arch-btn-primary arch-btn-sm"
                disabled={decidingId === est.id}
                onClick={() => approve(est)}
              >
                Approve
              </button>
            </div>
          </>
        ) : null}
      </div>
    );
  };

  if (!open) return null;

  // A legacy architecture may have been approved before automatic hand-offs
  // existed. Keep a manual recovery path, but only while the CURRENT revision
  // has neither a document nor a live run. The ordinary approval path still
  // starts the estimate on the server without this button.
  const canRecoverEstimate = workspace && canEstimate && architectureApproved &&
    estimateListLoadedFor === recoveryKey && resumeChecked && !loading && !running && !estimateRunning &&
    (historicalOnly || estimates.length === 0);

  // The document editor takes over the whole workspace; the modal steps
  // aside until it closes. (Editing is estimator-only, so only reachable
  // from the canEstimate list below.)
  if (editingId && canViewEstimate) {
    // projectId feeds the editor's side-by-side architecture pane
    // (DESIGN22 §D2) — the poster SVG is fetched from the project.
    return (
      <EstimateDocEditor
        estimateId={editingId}
        projectId={projectId}
        onClose={closeEditor}
        embedded={workspace}
        onDraftStateChange={state => { documentDraftDirty.current = state.dirty; onDraftStateChange?.(state); }}
        active={active}
        revision={revision}
        defaultArchitectureOpen={workspace}
        reviewPanel={workspace && !historicalOnly && estimates.find((est) => est.id === editingId)?.status === 'review'
          ? renderReview(estimates.find((est) => est.id === editingId)!)
          : undefined}
        approvalStatus={approvals[editingId]}
        approvalUserId={user?.id}
        onChanged={workspace ? () => { void load(); onChangedRef.current?.(); } : undefined}
      />
    );
  }

  // ── Neither sub-role: a friendly notice, no estimate UI ──
  if (noAccess) {
    return (
      <div
        className={workspace ? "arch-est-workspace arch-est-workspace-list" : "arch-modal-overlay"}
        onMouseDown={(e) => {
          if (!workspace && e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className="arch-modal"
          role={workspace ? "region" : "dialog"}
          aria-modal={workspace ? undefined : true}
          aria-label={'Estimates — ' + projectName}
        >
          {workspace ? <StageHeader title="Estimate" status="Unavailable" decision={<StageFooter placement="header" stage="estimate" pending title="Estimate unavailable"/>}/> : <div className="arch-modal-head">
            <span className="arch-modal-title">{workspace ? 'Estimate' : 'Estimates — ' + projectName}</span>
            {!workspace ? <button
              className="arch-btn arch-btn-ghost arch-btn-sm"
              aria-label="Close"
              onClick={onClose}
            >
              ✕
            </button> : null}
          </div>}
          <div className="arch-modal-body">
            <div className="arch-est-notice">
              Estimates are not enabled for your account — ask the administrator
              for the Generator or Estimator role.
            </div>
          </div>
          {workspace && canEstimate ? <><StageAiButton disabled onClick={() => {}}/></> : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className={workspace ? "arch-est-workspace arch-est-workspace-list" : "arch-modal-overlay"}
      onMouseDown={(e) => {
        if (!workspace && e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        className="arch-modal"
        role={workspace ? "region" : "dialog"}
        aria-modal={workspace ? undefined : true}
        aria-label={'Estimates — ' + projectName}
      >
        {workspace ? <StageHeader title="Estimate" status={estimates.length ? `${estimates.length} versions` : running ? 'Preparing' : undefined} actions={[
          { id: 'refresh', label: 'Refresh versions', icon: <RefreshCw size={17}/>, onClick: () => { void load(); }, disabled: loading },
        ]} decision={<StageFooter placement="header" stage="estimate" pending={estimates[0]?.status !== 'approved'} status={approvals[estimates[0]?.id || '']} userId={user?.id} title={estimates.length ? 'Estimate history' : running ? 'Preparing estimate' : 'Waiting for architecture'}/>}/> : <div className="arch-modal-head">
          <span className="arch-modal-title">{workspace ? 'Estimate' : 'Estimates — ' + projectName}</span>
          {!workspace ? <button
            className="arch-btn arch-btn-ghost arch-btn-sm"
            aria-label="Close"
            disabled={running}
            onClick={requestClose}
          >
            ✕
          </button> : null}
        </div>}

        <div className="arch-modal-body">
          {activeRun ? (
            <div className="arch-est-stepper">
              <RunStepper
                steps={EST_STEPS}
                stepDesc={EST_STEP_DESC}
                active={stageStep(activeRun.stage)}
                elapsedMs={elapsedMs}
                heading={'Estimating — ' + projectName}
                modelChip={modelDisplayEstimate(activeRun.model || model)}
                stage={activeRun.stage}
                updatedAt={activeRun.updated_at}
                onStop={canEstimate ? stopRun : undefined}
                caption="You can leave this page — the estimate continues on the server."
              />
            </div>
          ) : null}

          {!workspace && canEstimate && <div className="arch-est-launch">
            <label className="arch-inline-field">
              <span>Model</span>
              <select
                aria-label="Estimate model"
                value={model}
                disabled={running}
                onChange={(e) => setModel(e.target.value)}
              >
                {ESTIMATE_MODELS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="arch-btn arch-btn-primary"
              disabled={running || !architectureApproved}
              onClick={generate}
            >
              {running ? <span className="arch-spinner" /> : null}
              <span>{running ? 'Estimating…' : 'Send for estimate'}</span>
            </button>
            {/* Gate hint (§F3): no dead-end — tell the viewer exactly what to
                do. Viewing existing estimates below stays available. */}
            {!architectureApproved ? (
              <span className="arch-est-gate-hint">
                Approve the architecture first — then send for estimate.
              </span>
            ) : null}
          </div>}

          {runError ? (
            <div className="arch-est-run-error" role="alert">
              ✕ {runError}
            </div>
          ) : null}

          {canRecoverEstimate ? (
            <div className="arch-est-recovery" role="group" aria-label="Generate current estimate">
              <span className="arch-est-recovery-copy">No estimate for this architecture yet.</span>
              <label className="arch-inline-field">
                <span>Model</span>
                <select aria-label="Estimate model" value={model} onChange={(e) => setModel(e.target.value)}>
                  {ESTIMATE_MODELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </label>
              <button type="button" className="arch-btn arch-btn-primary" onClick={generate}>Generate estimate</button>
            </div>
          ) : null}

          {/* Generator-only: an estimate is with the estimator. */}
          {!canEstimate && pendingReview > 0 ? (
            <div className="arch-est-review-banner" role="status">
              ⏳ Estimate under review — you'll see it once the estimator approves.
            </div>
          ) : null}

          <div className="arch-est-list">
            {historicalOnly ? (
              canRecoverEstimate ? null : <div className="arch-empty">{running || estimateRunning ? 'Preparing the current estimate…' : 'Earlier estimates remain in history.'}</div>
            ) : loading && !estimates.length ? (
              <div className="arch-empty">Loading estimates…</div>
            ) : !estimates.length ? (
              canRecoverEstimate ? null : <div className="arch-empty">
                {canEstimate
                  ? workspace ? running || estimateRunning ? 'Preparing the estimate…' : 'A new estimate starts automatically after approval.' : 'No estimates yet. Pick a model and press “Send for estimate”.'
                  : pendingReview > 0
                    ? 'No approved estimates yet — one is under review.'
                    : 'No approved estimates yet. Pick a model and press “Send for estimate”.'}
              </div>
            ) : (
              estimates.map((est) => (
                <div
                  className={'arch-est-row' + (est.id === flashId ? ' flash' : '')}
                  key={est.id}
                >
                  <div className="arch-est-row-head">
                    <span className={'arch-est-chip ' + est.status}>
                      {STATUS_LABEL[est.status]}
                    </span>
                    <span className="arch-est-model">{modelDisplayEstimate(est.model)}</span>
                    <span className="arch-est-date">{fmtDate(est.created_at)}</span>
                  </div>

                  <div className="arch-est-title">{est.title}</div>

                  {/* The reviewer NAME is root-only (backend nulls it for
                      everyone else). Non-root sees the acceptance FACT + WHEN
                      («Accepted · <date, time>») — the fact, not the person. */}
                  {est.reviewed_by_name ? (
                    <div className="arch-est-meta">
                      {est.status === 'approved' ? 'Approved by ' : 'Reviewed by '}
                      {est.reviewed_by_name}
                      {est.reviewed_at ? ' · ' + fmtDate(est.reviewed_at) : ''}
                    </div>
                  ) : est.reviewed_at ? (
                    <div className="arch-est-meta">
                      Accepted · {fmtDate(est.reviewed_at)}
                    </div>
                  ) : null}

                  {est.review_note ? (
                    <div className="arch-est-note">“{est.review_note}”</div>
                  ) : null}

                  <div className="arch-est-actions">
                    {/* Approved versions remain available for reading. */}
                    {canViewEstimate ? (
                      <button
                        className="arch-btn arch-btn-outline arch-btn-sm"
                        title={est.status === 'approved' ? 'Approved version · read only' : undefined}
                        onClick={() => setEditingId(est.id)}
                      >
                        {est.status === 'approved' || !canEstimate ? 'View' : 'Edit'}
                      </button>
                    ) : null}
                    {est.has_docx ? (
                      <button
                        className="arch-btn arch-btn-outline arch-btn-sm"
                        disabled={downloadingId === est.id}
                        onClick={() => download(est)}
                      >
                        {downloadingId === est.id ? 'Preparing…' : 'Download .docx'}
                      </button>
                    ) : null}
                  </div>

                  {/* Approve + knowledge feedback — estimator-only, in review.
                      DESIGN23 §C3: the approve control only appears for an
                      assigned approver who hasn't voted yet; everyone else
                      gets the counter and, when the policy excludes them, the
                      muted «waiting for…» line. With no policy configured the
                      block is exactly what it has always been. */}
                  {canApproveEstimate && est.status === 'review'
                    ? renderReview(est)
                    : null}
                </div>
              ))
            )}
          </div>
        </div>
        {workspace && canEstimate ? <><StageAiButton disabled={!estimates.length || !canEstimate} onClick={() => { if (estimates[0]) setEditingId(estimates[0].id); }}/></> : null}
      </div>
    </div>
  );
}
